import React, { useState, useEffect } from 'react';
import { rrhhPublicoService } from '../services/rrhhInventario';
import { Send, Search, Plus, X, CheckCircle2, ClipboardList } from 'lucide-react';

// Formulario público (sin login) para que los colaboradores pidan EPP,
// uniformes, documentos u otros requerimientos a RRHH y consulten su estado.

const TIPOS_POR_DEFECTO = [
    { value: 'epp_uniforme', label: 'EPP / Uniforme' },
    { value: 'reposicion', label: 'Reposición (desgaste, pérdida o talla)' },
    { value: 'documento', label: 'Documento (constancia, boleta, certificado)' },
    { value: 'otro', label: 'Otro requerimiento' },
];
const TIPOS_CON_PRODUCTOS = ['epp_uniforme', 'reposicion'];

const ESTADOS = {
    pendiente: { label: 'Pendiente', cls: 'bg-amber-100 text-amber-700', texto: 'RRHH aún no la revisa.' },
    en_proceso: { label: 'En proceso', cls: 'bg-blue-100 text-blue-700', texto: 'RRHH la está atendiendo.' },
    atendida: { label: 'Atendida', cls: 'bg-green-100 text-green-700', texto: 'Tu solicitud fue atendida.' },
    rechazada: { label: 'Rechazada', cls: 'bg-red-100 text-red-700', texto: 'Tu solicitud no pudo ser atendida.' },
};

const campoCls = 'px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white text-gray-900';
const inputCls = `w-full ${campoCls}`;
const errorDe = (err, porDefecto) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg.replace(/^Value error, /, '');
    return porDefecto;
};

// ─── Nueva solicitud ────────────────────────────────────────────────────────

const FormularioSolicitud = ({ tipos, catalogo }) => {
    const [documento, setDocumento] = useState('');
    const [tipo, setTipo] = useState('epp_uniforme');
    const [lineas, setLineas] = useState([{ itemId: '', cantidad: 1 }]);
    const [detalle, setDetalle] = useState('');
    const [telefono, setTelefono] = useState('');
    const [correo, setCorreo] = useState('');
    const [website, setWebsite] = useState(''); // honeypot
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState('');
    const [creada, setCreada] = useState(null);

    const conProductos = TIPOS_CON_PRODUCTOS.includes(tipo);
    const actualizarLinea = (idx, campo, valor) =>
        setLineas((prev) => prev.map((l, i) => (i === idx ? { ...l, [campo]: valor } : l)));

    const enviar = async (e) => {
        e.preventDefault();
        setError('');
        if (documento.trim().length < 4) { setError('Ingresa tu número de documento'); return; }
        const items = conProductos
            ? lineas.filter((l) => l.itemId).map((l) => ({ item_id: Number(l.itemId), cantidad: Number(l.cantidad) || 1 }))
            : [];
        if (conProductos && items.length === 0) { setError('Elige al menos un producto'); return; }
        if (!conProductos && !detalle.trim()) { setError('Describe tu requerimiento'); return; }

        setEnviando(true);
        try {
            const resultado = await rrhhPublicoService.enviarSolicitud({
                documento: documento.trim(),
                tipo,
                items,
                detalle: detalle.trim() || null,
                telefono: telefono.trim() || null,
                correo: correo.trim() || null,
                website: website || null,
            });
            setCreada(resultado);
        } catch (err) {
            setError(errorDe(err, 'No se pudo enviar la solicitud. Inténtalo de nuevo.'));
        } finally {
            setEnviando(false);
        }
    };

    if (creada) {
        return (
            <div className="text-center py-6">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-gray-900">¡Solicitud enviada{creada.empleado_nombre ? `, ${creada.empleado_nombre.split(' ')[0]}` : ''}!</h2>
                <p className="text-sm text-gray-500 mt-1">Guarda este código para consultar el estado:</p>
                <p className="text-3xl font-mono font-bold tracking-widest text-blue-700 my-4 select-all">{creada.codigo}</p>
                <p className="text-xs text-gray-400 mb-6">Lo necesitarás junto con tu documento en "Consultar estado".</p>
                <button
                    onClick={() => { setCreada(null); setDetalle(''); setLineas([{ itemId: '', cantidad: 1 }]); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                >
                    Enviar otra solicitud
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={enviar} className="space-y-4">
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">N° de documento (DNI / CE)</label>
                <input value={documento} onChange={(e) => setDocumento(e.target.value)} inputMode="numeric" autoComplete="off" className={inputCls} placeholder="Ej: 12345678" />
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">¿Qué necesitas?</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {tipos.map((t) => (
                        <button
                            type="button"
                            key={t.value}
                            onClick={() => setTipo(t.value)}
                            className={`text-left px-3 py-2.5 rounded-lg text-sm border ${
                                tipo === t.value ? 'bg-blue-50 border-blue-400 text-blue-800 font-semibold' : 'border-gray-300 text-gray-600'
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {conProductos && (
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Productos</label>
                    <div className="space-y-2">
                        {lineas.map((linea, idx) => (
                            <div key={idx} className="grid grid-cols-[minmax(0,1fr)_5rem_1rem] items-center gap-2">
                                <select value={linea.itemId} onChange={(e) => actualizarLinea(idx, 'itemId', e.target.value)} className={campoCls}>
                                    <option value="">Elegir producto...</option>
                                    {catalogo.map((i) => (
                                        <option key={i.id} value={i.id}>{i.descripcion}{i.talla ? ` — talla ${i.talla}` : ''}</option>
                                    ))}
                                </select>
                                <input type="number" min="1" max="50" value={linea.cantidad} onChange={(e) => actualizarLinea(idx, 'cantidad', e.target.value)} className={campoCls} aria-label="Cantidad" />
                                {lineas.length > 1 && (
                                    <button type="button" onClick={() => setLineas((prev) => prev.filter((_, i) => i !== idx))} className="text-gray-400 hover:text-red-600" aria-label="Quitar">
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    {lineas.length < 20 && (
                        <button type="button" onClick={() => setLineas((prev) => [...prev, { itemId: '', cantidad: 1 }])} className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center">
                            <Plus className="h-3.5 w-3.5 mr-1" /> Agregar otro producto
                        </button>
                    )}
                </div>
            )}

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                    {conProductos ? 'Comentario (opcional)' : 'Detalle del requerimiento'}
                </label>
                <textarea
                    value={detalle}
                    onChange={(e) => setDetalle(e.target.value)}
                    rows={3}
                    maxLength={2000}
                    className={inputCls}
                    placeholder={conProductos ? 'Ej: el anterior se rompió, necesito otra talla...' : 'Ej: constancia de trabajo para trámite bancario'}
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Celular (opcional)</label>
                    <input value={telefono} onChange={(e) => setTelefono(e.target.value)} inputMode="tel" maxLength={30} className={inputCls} />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Correo (opcional)</label>
                    <input type="email" value={correo} onChange={(e) => setCorreo(e.target.value)} maxLength={255} className={inputCls} />
                </div>
            </div>

            {/* Campo trampa para bots: oculto a personas */}
            <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute -left-[9999px] h-0 w-0 opacity-0"
            />

            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}

            <button
                type="submit"
                disabled={enviando}
                className="w-full inline-flex items-center justify-center py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
            >
                <Send className="h-4 w-4 mr-2" /> {enviando ? 'Enviando...' : 'Enviar solicitud'}
            </button>
        </form>
    );
};

// ─── Consultar estado ───────────────────────────────────────────────────────

const ConsultaSolicitud = () => {
    const [codigo, setCodigo] = useState('');
    const [documento, setDocumento] = useState('');
    const [buscando, setBuscando] = useState(false);
    const [error, setError] = useState('');
    const [resultado, setResultado] = useState(null);

    const consultar = async (e) => {
        e.preventDefault();
        setError('');
        setResultado(null);
        if (!codigo.trim() || documento.trim().length < 4) { setError('Ingresa el código y tu documento'); return; }
        setBuscando(true);
        try {
            setResultado(await rrhhPublicoService.consultarSolicitud(codigo.trim(), documento.trim()));
        } catch (err) {
            setError(errorDe(err, 'No se pudo consultar. Inténtalo de nuevo.'));
        } finally {
            setBuscando(false);
        }
    };

    const estado = resultado ? ESTADOS[resultado.estado] : null;

    return (
        <div className="space-y-4">
            <form onSubmit={consultar} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Código</label>
                        <input value={codigo} onChange={(e) => setCodigo(e.target.value.toUpperCase())} placeholder="SOL-XXXXXX" className={`${inputCls} font-mono`} />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">N° de documento</label>
                        <input value={documento} onChange={(e) => setDocumento(e.target.value)} inputMode="numeric" className={inputCls} />
                    </div>
                </div>
                {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
                <button type="submit" disabled={buscando} className="w-full inline-flex items-center justify-center py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60">
                    <Search className="h-4 w-4 mr-2" /> {buscando ? 'Consultando...' : 'Consultar'}
                </button>
            </form>

            {resultado && (
                <div className="border border-gray-200 rounded-xl p-4 text-sm space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="font-mono font-semibold text-gray-800">{resultado.codigo}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${estado?.cls}`}>{estado?.label || resultado.estado}</span>
                    </div>
                    <p className="text-gray-500">{estado?.texto}</p>
                    {resultado.detalles.length > 0 && (
                        <ul className="list-disc list-inside text-gray-700">
                            {resultado.detalles.map((d) => <li key={d.id}>{d.descripcion} × {d.cantidad}</li>)}
                        </ul>
                    )}
                    {resultado.detalle && <p className="text-gray-600 whitespace-pre-line">{resultado.detalle}</p>}
                    {resultado.respuesta && (
                        <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">Respuesta de RRHH</p>
                            <p className="text-gray-800 whitespace-pre-line">{resultado.respuesta}</p>
                        </div>
                    )}
                    <p className="text-xs text-gray-400">Enviada el {new Date(resultado.created_at).toLocaleString('es-PE')}</p>
                </div>
            )}
        </div>
    );
};

// ─── Página ─────────────────────────────────────────────────────────────────

const SolicitudColaboradorPage = () => {
    const [pestana, setPestana] = useState('nueva');
    const [tipos, setTipos] = useState(TIPOS_POR_DEFECTO);
    const [catalogo, setCatalogo] = useState([]);

    useEffect(() => {
        rrhhPublicoService.tipos().then((t) => { if (t?.length) setTipos(t); }).catch(() => {});
        rrhhPublicoService.catalogo().then(setCatalogo).catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-xl mx-auto">
                <div className="text-center mb-6">
                    <div className="inline-flex bg-blue-100 p-3 rounded-2xl mb-3">
                        <ClipboardList className="h-7 w-7 text-blue-700" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">Solicitudes a Recursos Humanos</h1>
                    <p className="text-sm text-gray-500 mt-1">Pide EPP, uniformes, documentos u otros requerimientos. No necesitas cuenta.</p>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
                    <div className="grid grid-cols-2 border-b border-gray-200">
                        {[['nueva', 'Nueva solicitud'], ['consulta', 'Consultar estado']].map(([valor, label]) => (
                            <button
                                key={valor}
                                onClick={() => setPestana(valor)}
                                className={`py-3 text-sm font-semibold ${
                                    pestana === valor ? 'text-blue-700 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className="p-5 relative">
                        {pestana === 'nueva' ? <FormularioSolicitud tipos={tipos} catalogo={catalogo} /> : <ConsultaSolicitud />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SolicitudColaboradorPage;
